/**
 * Foreground push handler for RFID tap-to-charge and the general charge
 * lifecycle. See `meander-backend/docs/RFID_TAP_TO_CHARGE_FRONTEND_CONTRACT.md`
 * §3.5 for the wire format and the user-supplied design notes.
 *
 * Behaviour summary:
 *
 *  • `rfid_tap` AUTHORIZED → refresh active charges, surface a toast, and
 *    navigate to ChargingScreen for the matching `order_uuid`. We treat the
 *    push as a hint; ChargingScreen is responsible for waiting on
 *    `getActiveChargeData` polling before calling the session "live".
 *  • `rfid_tap` MATCHED_PENDING_CLAIM → success toast; the AddRfidTag /
 *    MyRfidTags / RfidCardSettings screens already poll `GET /rfid/cards`
 *    so we let them advance their UI on next tick.
 *  • `rfid_tap` denials (any `DENIED_*` / `UNKNOWN_TAG`) → error toast that
 *    surfaces `data.reason` when the backend provides one, then deep-link
 *    via the existing `NotificationNavigationHandler` mapping.
 *  • `charge_started` / `charge_scheduled` → refresh active charges. If we
 *    haven't already navigated for the same `orderId` (RFID AUTHORIZED is
 *    fired in parallel by the backend, see contract), open ChargingScreen.
 *  • `charge_completed` → refresh active charges (Live Activity end is
 *    handled by `NotificationNavigationHandler.handleForegroundNotification`).
 *
 * The dedupe cache short-circuits the `rfid_tap` AUTHORIZED → `charge_started`
 * pair the backend emits ~back-to-back (see contract §"Timing"). The same
 * cache is reset on app resume so a stale entry from a previous session can
 * never block a fresh start push.
 */

import { Platform } from 'react-native';
import store from '../store';
import { getActiveCharges } from '../store/actions/user-actions';
import { notificationService } from './notification-service';
import NotificationNavigationHandler from './NotificationNavigationHandler';
import type { NotificationData } from './ExpoNotificationService';

const RootNavigation = require('../../RootNavigation');

// Window during which a follow-up `charge_started` for the same orderId is
// considered a duplicate of an `rfid_tap` AUTHORIZED we just navigated for.
// The backend fires the two pushes within a few hundred ms of each other.
const NAVIGATE_DEDUPE_WINDOW_MS = 15_000;

const navigatedOrders = new Map<string, number>();

function markNavigated(orderId: string) {
  navigatedOrders.set(orderId, Date.now());
}

function wasRecentlyNavigated(orderId: string): boolean {
  const ts = navigatedOrders.get(orderId);
  if (!ts) return false;
  if (Date.now() - ts > NAVIGATE_DEDUPE_WINDOW_MS) {
    navigatedOrders.delete(orderId);
    return false;
  }
  return true;
}

/**
 * Reset the dedupe cache. Call on app resume / authentication change so a
 * stale entry can't suppress a legitimate start push from a new session.
 */
export function resetForegroundPushDedupe(): void {
  navigatedOrders.clear();
}

/**
 * The contract uses both `order_uuid` (RFID payloads) and `orderId` (charge
 * lifecycle payloads). Normalize so the rest of the handler doesn't have to.
 */
function readOrderId(data: NotificationData): string | undefined {
  if (typeof data.order_uuid === 'string' && data.order_uuid.length > 0) {
    return data.order_uuid;
  }
  if (typeof data.orderId === 'string' && data.orderId.length > 0) {
    return data.orderId;
  }
  return undefined;
}

function currentRouteName(): string | null {
  try {
    return RootNavigation.getCurrentRoute()?.name ?? null;
  } catch {
    return null;
  }
}

function navigateToChargingScreen(orderId: string, chargerUuid?: string | null): void {
  if (currentRouteName() === 'ChargingScreen') {
    // Already on the right screen; the screen's own `getActiveCharges` +
    // polling loop will hydrate the new order. Just dedupe future pushes.
    markNavigated(orderId);
    return;
  }

  // ChargingScreen reads `route.params.order.id` directly, so we have to
  // pass an `order` stub (matches the existing
  // `NotificationNavigationHandler` AUTHORIZED branch). The full record is
  // hydrated from `state.user.activeCharges` once `getActiveCharges`
  // resolves.
  const params: any = { order: { id: orderId } };
  if (chargerUuid) params.chargerUuid = chargerUuid;

  try {
    RootNavigation.navigate('ChargingScreen', params);
    markNavigated(orderId);
  } catch (e) {
    console.warn('[RfidPushForegroundHandler] Failed to navigate to ChargingScreen:', e);
  }
}

function refreshActiveCharges(): void {
  try {
    store.dispatch(getActiveCharges() as any);
  } catch (e) {
    console.warn('[RfidPushForegroundHandler] getActiveCharges dispatch failed:', e);
  }
}

function handleRfidTap(data: NotificationData): boolean {
  if (data.type !== 'rfid_tap') return false;

  const outcome = data.outcome as string | undefined;
  const reason = typeof data.reason === 'string' ? data.reason : undefined;
  const orderId = readOrderId(data);
  const chargerUuid = typeof data.charger_uuid === 'string' ? data.charger_uuid : null;

  switch (outcome) {
    case 'AUTHORIZED': {
      notificationService.sendNotification(
        'success',
        'Charging started',
        'Tap authorized — opening your active session…',
      );
      refreshActiveCharges();
      if (orderId) navigateToChargingScreen(orderId, chargerUuid);
      return true;
    }
    case 'MATCHED_PENDING_CLAIM': {
      notificationService.sendNotification(
        'success',
        'Tag paired',
        'Your new RFID tag is paired. Tap it again at the charger to start charging.',
      );
      // AddRfidTag / RfidCardSettings already poll `GET /rfid/cards`; they
      // pick up the ACTIVE transition on their next tick.
      return true;
    }
    case 'DENIED_NO_PM':
    case 'DENIED_DISABLED':
    case 'DENIED_NO_ACCESS':
    case 'DENIED_INTERNAL_ERROR':
    case 'DENIED_CHARGER_BLOCKED':
    case 'UNKNOWN_TAG': {
      const fallbackTitle =
        outcome === 'UNKNOWN_TAG' ? 'Unrecognized tap' : 'Tap not authorized';
      notificationService.sendNotification(
        'error',
        fallbackTitle,
        reason || 'See the app for details.',
      );
      // Foreground deep-link to the relevant settings screen so the user
      // can resolve immediately. Wrap in try/catch so a navigator-not-ready
      // failure doesn't bubble.
      try {
        NotificationNavigationHandler.navigateFromNotification(data).catch((e) => {
          console.warn('[RfidPushForegroundHandler] denial navigate failed:', e);
        });
      } catch (e) {
        console.warn('[RfidPushForegroundHandler] denial navigate threw:', e);
      }
      return true;
    }
    default:
      return true;
  }
}

function handleChargeLifecycle(data: NotificationData): boolean {
  const type = data.type;
  if (type !== 'charge_started' && type !== 'charge_scheduled') return false;

  const orderId = readOrderId(data);

  // Always refresh active charges so the side menu / ActiveChargeCard pick
  // up RFID-initiated sessions even if we suppress the navigate below.
  refreshActiveCharges();

  if (!orderId) return true;

  if (wasRecentlyNavigated(orderId)) {
    // RFID AUTHORIZED already routed us; the backend's follow-up
    // `charge_started` is the same session.
    return true;
  }

  // `charge_scheduled` (start >5s in future) shouldn't auto-navigate — let
  // the user decide. The toast from the OS (or the foreground toast above)
  // is enough.
  if (type === 'charge_started') {
    navigateToChargingScreen(orderId, null);
  } else {
    // Track scheduled charges so a same-id `charge_started` later still
    // works normally (don't dedupe, just keep cache clean).
  }
  return true;
}

function handleChargeCompleted(data: NotificationData): boolean {
  if (data.type !== 'charge_completed' && data.type !== 'charging_complete') return false;
  // Live Activity ending is handled by NotificationNavigationHandler. We
  // only need to clear active charges from the global store.
  refreshActiveCharges();
  return true;
}

/**
 * Entry point used by App.tsx's foreground notification handler.
 * Returns true if the push was an RFID/charge type (the caller can stop).
 */
export function handleForegroundRfidOrChargePush(data: NotificationData | undefined | null): boolean {
  if (!data || typeof data !== 'object') return false;
  // Web push surface uses Service Worker today; this handler is a no-op
  // on web since active-charge UI isn't rendered there yet.
  if (Platform.OS === 'web') return false;

  try {
    if (handleRfidTap(data)) return true;
    if (handleChargeLifecycle(data)) return true;
    if (handleChargeCompleted(data)) return true;
  } catch (e) {
    console.error('[RfidPushForegroundHandler] handler threw:', e);
  }
  return false;
}

export const __test__ = {
  navigatedOrders,
  NAVIGATE_DEDUPE_WINDOW_MS,
};
